import React, { useCallback, useEffect, useState } from "react";
import swal from "sweetalert2";
import CS from "../../../services/commonService";
import BreadCrumbs from "../../common/breadcrumbs";
import Modal from "../../common/Modal";
import Table from "../../common/table";
import SingleStatusToggle from "../../common/singleStatusToggle";
import SearchBox from "../../common/searchBox";
import AddUpdate from "./addUpdate";
import {
	useGetAllCuisine,
	useDeleteCuisineById,
	useUpdateFoodCuisine,
} from "../../../queryHooks/useCuisine";
import { getCurrentTimezoneDate } from "../../../util/columnsOrdering";
import qs from "qs";
import ReactTooltip from "react-tooltip";
import Icon from "../../icons/index";
import { useStore } from "../../../zustand/store";
import { pageToLoadOnDelete } from "../../../helpers";

//	Pre-defined values
const columnKey = "cuisines";
//	Component starts
const Cuisines = () => {
	//	State values
	const [tableData, setTableData] = useState();
	const [cuisine, setCuisine] = useState({});
	const [open, setOpen] = useState(false);
	const setLoading = useStore((state) => state.setLoading);
	const [pageNow, setPageNow] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(10);

	/******************************************************************************************
	@Purpose    :   To get the cuisines list
	*******************************************************************************************/
	const [getCuisinePayload, setCuisinePayload] = useState({
		pagination: true,
		items_per_page: itemsPerPage,
		page: pageNow,
		sort: { name: "DESC" },
	});

	const {
		data: dataGetCuisine,
		isLoading,
		refetch: getCuisines,
	} = useGetAllCuisine(qs.stringify(getCuisinePayload));

	useEffect(() => {
		if (dataGetCuisine) {
			setLoading(false);
			if (dataGetCuisine.status === "success") {
				let respData = dataGetCuisine.data.data;
				respData.page = pageNow;
				respData.items_per_page = itemsPerPage;
				setTableData(dataGetCuisine.data.data);
			}
		}
	}, [dataGetCuisine]);

	/***************************************************
	 * @DeleteCuisine
	 */

	const {
		mutateAsync: deleteCuisine,
		error: errorDeleteCuisine,
		data: dataDeleteCuisine,
	} = useDeleteCuisineById();

	useEffect(() => {
		if (dataDeleteCuisine) {
			setLoading(false);
			if (dataDeleteCuisine.status === "success") {
				let _page = pageToLoadOnDelete(
					tableData?.total_items,
					1,
					pageNow,
					itemsPerPage
				);
				if (_page === pageNow) getCuisines();
				setPageNow(_page);
				setCuisinePayload({ ...getCuisinePayload, page: _page });
				CS.showSuccessMessage(dataDeleteCuisine.message);
			} else if (dataDeleteCuisine.status === "fail") {
				CS.showErrorMessage(dataDeleteCuisine.message);
			}
		}
	}, [dataDeleteCuisine]);

	useEffect(() => {
		if (errorDeleteCuisine) {
			setLoading(false);
			CS.showErrorMessage(errorDeleteCuisine.response?.data?.message);
		}
	}, [errorDeleteCuisine]);

	/****************************************************************************
	 * update status
	 ****************************************************************************/

	const handleChangeStatus = (id, status) => {
		const body = {
			is_active: status == 1 ? false : true,
		};
		setLoading(true);
		updateCuisine({
			body,
			id,
		});
	};

	const {
		mutateAsync: updateCuisine,
		error: errorUpdateCuisine,
		data: dataUpdateCuisine,
	} = useUpdateFoodCuisine();

	useEffect(() => {
		if (dataUpdateCuisine) {
			setLoading(false);
			if (dataUpdateCuisine.status === "success") {
				getCuisines();
				CS.showSuccessMessage(dataUpdateCuisine.message);
			} else if (dataUpdateCuisine.status === "fail") {
				CS.showErrorMessage(dataUpdateCuisine.message);
			}
		}
	}, [dataUpdateCuisine]);

	useEffect(() => {
		if (errorUpdateCuisine) {
			setLoading(false);
			CS.showErrorMessage(errorUpdateCuisine.response?.data?.message);
		}
	}, [errorUpdateCuisine]);

	/******************************************************************************************
	@Purpose    :   To close the modal
	*******************************************************************************************/
	const columns = [
		{
			label: "Cuisine Name",
			type: "string",
			sort: true,
			alias: "name",
			path: "cuisineName",
			content: (item) => (
				<a
					href="#"
					onClick={() => {
						setOpen(true);
						setCuisine(item);
					}}
				>
					<div className="d-flex align-items-center">
						<div className="d-flex flex-column flex-wrap">
							<span className="pl-2">{item.name}</span>
						</div>
					</div>
				</a>
			),
		},
		{
			label: "Created On",
			type: "string",
			sort: true,
			path: "created_at",
			alias: "created_at",
			filterDate: true,
			content: (item) => getCurrentTimezoneDate(item.created_at, true),
		},
		{
			label: "Status",
			type: "string",
			aliasFilter: "is_active",
			filterOptions: [
				{ value: 1, label: "Active" },
				{ value: 0, label: "Inactive" },
			],
			content: (item) => (
				<SingleStatusToggle
					_id={item.cuisine_id}
					status={item.is_active == 1 ? true : false}
					onToggleChange={() =>
						handleChangeStatus([item.cuisine_id], item.is_active)
					}
					htmlFor={item.cuisine_id}
				/>
			),
		},
		{
			label: "Action",
			type: "boolean",
			content: (item) => (
				<>
					<Icon
						type="edit"
						data-tip
						data-for="edit"
						onClick={() => {
							setLoading(false);
							setOpen(true);
							setCuisine(item);
						}}
					/>
					<ReactTooltip id="edit" place="top" effect="solid">
						Edit
					</ReactTooltip>
					<Icon
						type="delete"
						data-tip
						data-for="delete"
						onClick={() =>
							swal
								.fire({
									title: "Are you sure you want to delete?",
									icon: "warning",
									showCancelButton: true,
									confirmButtonColor: "#3085d6",
									cancelButtonColor: "#d33",
									confirmButtonText: "Yes, delete it!",
								})
								.then((result) => {
									if (result.isConfirmed) {
										setLoading(true);
										deleteCuisine(item.cuisine_id);
									}
								})
						}
					/>
					<ReactTooltip id="delete" place="top" effect="solid">
						Delete
					</ReactTooltip>
				</>
			),
		},
	];

	const closeModal = useCallback(
		(reload) => {
			setOpen(false);
			setCuisine({});
			if (reload === true) getCuisines();
		},
		[getCuisines]
	);

	return (
		<>
			<BreadCrumbs title="Master Management" data={[{ label: "Cuisines" }]} />
			<div className="dataTables_wrapper">
				<div className="content-area position-relative">
					<div className="grid">
						<div className="grid-content">
							<div className="user-listing-filterOptions mb-2 mt-3 d-block">
								<div className="row mb-2">
									<div className="col-sm-8 mb-2 position-static">
										<div className="left-buttons d-flex">
											<SearchBox
												columnKey={columnKey}
												search={(value) => {
													setPageNow(1);
													setCuisinePayload({
														...getCuisinePayload,
														search: {
															name: !value && value === "" ? undefined : value,
														},
														page: 1,
													});
												}}
											/>
										</div>
									</div>
									<div className="col-sm-4">
										<div className="right-buttons d-flex justify-content-end">
											<button
												onClick={() => setOpen(true)}
												type="button"
												className="btn glow-primary btn-primary"
											>
												<i className="bx bx-user-plus d-lg-none" />
												<span className="d-none d-sm-none d-lg-inline-block">
													Add Cuisine
												</span>
											</button>
										</div>
									</div>
								</div>
							</div>
							<Modal open={open} onCloseModal={closeModal}>
								<AddUpdate
									cuisine={cuisine}
									columnKey={columnKey}
									onCloseModal={closeModal}
									getCuisines={getCuisines}
								/>
							</Modal>
							<Table
								columnKey={columnKey}
								columns={columns}
								tableData={tableData}
								sortCol={(obj) => {
									setPageNow(1);
									setCuisinePayload({
										...getCuisinePayload,
										sort: {
											[obj.alias]:
												!obj.sort && obj.sort === "" ? undefined : obj.sort,
										},
										page: 1,
									});
								}}
								setCurrentPage={(page) => {
									setPageNow(page);
									setCuisinePayload({ ...getCuisinePayload, page });
								}}
								setCurrentLimit={(items_per_page) => {
									setPageNow(1);
									setItemsPerPage(items_per_page);
									setCuisinePayload({
										...getCuisinePayload,
										items_per_page,
										page: 1,
									});
								}}
								loading={isLoading}
								filterValue={async (obj) => {
									if (obj.filter === null) {
										const resp = await CS.filterData(
											getCuisinePayload.filter,
											obj
										);
										setPageNow(1);
										setCuisinePayload({
											...getCuisinePayload,
											filter: resp,
											page: 1,
										});
									} else {
										setPageNow(1);
										setCuisinePayload({
											...getCuisinePayload,
											filter: {
												...getCuisinePayload.filter,
												[obj.alias]: obj.filter,
											},
											page: 1,
										});
									}
								}}
								filterDate={(dates) => {
									setPageNow(1);
									setCuisinePayload({
										...getCuisinePayload,
										filter_date: dates && {
											...getCuisinePayload?.filter_date,
											[dates.alias]: String(dates.filter),
										},
										page: 1,
									});
								}}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default Cuisines;
